import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

// Logo importeren
import logoLions from './assets/images/Logolions.jpg';

// Componenten
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import ScrollToTop from './components/utils/ScrollToTop';
import CookieConsent from './components/CookieConsent';

// Pagina's
import UnderConstruction from './pages/UnderConstruction';
import Home from './pages/Home';
import About from './pages/About';
import Projects from './pages/Projects';
import News from './pages/News';
import Events from './pages/Events';
import Contact from './pages/Contact';
import MemberLogin from './pages/MemberLogin';
import LedenDashboard from './pages/LedenDashboard';
import LedenBeheer from './pages/LedenBeheer';
import Prac from './pages/Prac';
import NotFound from './pages/NotFound';
import Membership from './pages/Membership';
import Membershipzonderheaderfooter from './pages/Membershipzonderheaderfooter';
import Contactformnieuwlid from './pages/Contactformnieuwlid';
import History from './pages/History';

// Context
import { ThemeProvider } from './context/ThemeContext';

// Component om Header conditionally te renderen
const AppContent: React.FC = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [darkMode, setDarkMode] = useState<boolean>(false);

  useEffect(() => {
    // Simuleer laadtijd
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Check for user preference on initial load
  useEffect(() => {
    if (
      localStorage.theme === 'dark' ||
      (!('theme' in localStorage) &&
        window.matchMedia('(prefers-color-scheme: dark)').matches)
    ) {
      setDarkMode(true);
      document.documentElement.classList.add('dark');
    } else {
      setDarkMode(false);
      document.documentElement.classList.remove('dark');
    }
  }, []);

  // Toggle dark mode
  const toggleDarkMode = () => {
    if (darkMode) {
      setDarkMode(false);
      document.documentElement.classList.remove('dark');
      localStorage.theme = 'light';
    } else {
      setDarkMode(true);
      document.documentElement.classList.add('dark');
      localStorage.theme = 'dark';
    }
  };

  // Check of we op de Under Construction pagina zijn
  const isUnderConstruction = location.pathname === '/';
  
  // Check of we op de Membership zonder header/footer pagina zijn
  const isMembershipWithoutHeaderFooter = location.pathname === '/lid-worden-zonder-header-footer';
  
  // Check of we op de Contactformulier nieuwe leden pagina zijn
  const isContactformNieuwlid = location.pathname === '/contactform-nieuwlid';
  
  // Check of we op de Leden Dashboard pagina zijn
  const isLedenDashboard = location.pathname === '/leden-dashboard';
  
  // Check of we op de Leden Beheer pagina zijn
  const isLedenBeheer = location.pathname === '/leden-beheer';
  
  // Check of we op de PRAC pagina zijn
  const isPrac = location.pathname === '/prac';
  
  // Check of we op een pagina zijn zonder header/footer
  const hideHeaderFooter = isUnderConstruction || isMembershipWithoutHeaderFooter || isContactformNieuwlid || isLedenDashboard || isLedenBeheer || isPrac;

  return (
    <div className="flex flex-col min-h-screen bg-white dark:bg-lions-dark text-gray-900 dark:text-gray-100 transition-colors duration-300">
      {loading ? (
        <div className="flex items-center justify-center min-h-screen bg-lions-light dark:bg-lions-dark">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <img 
              src={logoLions} 
              alt="Lions Club Hoorn De Doelen" 
              className="w-32 h-32 mx-auto mb-4"
            />
            <h1 className="text-2xl font-bold text-lions-blue">Lions Club Hoorn De Doelen</h1>
          </motion.div>
        </div>
      ) : (
        <>
          {/* Alleen Header tonen als we NIET op een pagina zonder header/footer zijn */}
          {!hideHeaderFooter && (
            <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
          )}
          <main className="flex-grow">
            <AnimatePresence mode="wait">
              <Routes>
                <Route path="/" element={<UnderConstruction />} />
                <Route path="/nieuw" element={<Home />} />
                <Route path="/over-ons" element={<About />} />
                <Route path="/projecten" element={<Projects />} />
                <Route path="/nieuws" element={<News />} />
                <Route path="/evenementen" element={<Events />} />
                                            <Route path="/contact" element={<Contact />} />
                <Route path="/leden-login" element={<MemberLogin />} />
                <Route path="/leden-dashboard" element={<LedenDashboard />} />
                <Route path="/leden-beheer" element={<LedenBeheer />} />
                <Route path="/prac" element={<Prac />} />
                <Route path="/lid-worden" element={<Membership />} />
                <Route path="/lid-worden-zonder-header-footer" element={<Membershipzonderheaderfooter />} />
                <Route path="/contactform-nieuwlid" element={<Contactformnieuwlid />} />
                <Route path="/geschiedenis" element={<History />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </AnimatePresence>
          </main>
          {/* Alleen Footer tonen als we NIET op een pagina zonder header/footer zijn */}
          {!hideHeaderFooter && <Footer />}
        </>
      )}
      
      {/* Cookie Consent - altijd tonen */}
      <CookieConsent />
    </div>
  );
};

function App() {
  return (
    <ThemeProvider>
      <Router>
        <ScrollToTop />
        <AppContent />
      </Router>
    </ThemeProvider>
  );
}

export default App; 